import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = 'http://localhost:5000';

function JourneyConfigForm({ token, userId, onStartJourney, onViewAllJourneys }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    facilitator_id: userId,
    team_member_ids: [userId],
    max_participants: 15,
    duration_days: 30,
    status: 'active',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createdConfig, setCreatedConfig] = useState(null);
  const [facilitators, setFacilitators] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [activatingJourney, setActivatingJourney] = useState(false);

  useEffect(() => {
    // Fetch facilitators and contributors
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const facilitatorUsers = response.data.users.filter(u => u.role === 'facilitator') || [];
        const contributorUsers = response.data.users.filter(u => u.role === 'contributor') || [];

        setFacilitators(facilitatorUsers);
        setContributors(contributorUsers);
      } catch (err) {
        console.error('Failed to fetch users:', err);
      }
    };

    if (token) {
      fetchUsers();
    }
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) : value,
    }));
  };

  const handleTeamMemberToggle = (memberId) => {
    setFormData(prev => ({
      ...prev,
      team_member_ids: prev.team_member_ids.includes(memberId)
        ? prev.team_member_ids.filter(id => id !== memberId)
        : [...prev.team_member_ids, memberId],
    }));
  };

  const validateForm = () => {
    if (!formData.title || formData.title.length < 5) {
      setError('Title must be at least 5 characters');
      return false;
    }
    if (formData.title.length > 100) {
      setError('Title cannot exceed 100 characters');
      return false;
    }
    if (formData.description && formData.description.length > 500) {
      setError('Description cannot exceed 500 characters');
      return false;
    }
    if (!formData.facilitator_id) {
      setError('Facilitator is required');
      return false;
    }
    if (formData.team_member_ids.length < 1) {
      setError('At least 1 team member is required');
      return false;
    }
    if (formData.max_participants < 3 || formData.max_participants > 100) {
      setError('Max participants must be between 3 and 100');
      return false;
    }
    if (formData.duration_days < 7 || formData.duration_days > 365) {
      setError('Duration must be between 7 and 365 days');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/journey-configs`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setCreatedConfig(response.data.config);
      setSuccess(`✓ Journey '${response.data.config.title}' created! Ready to activate and get started?`);

      // Reset form
      setFormData({
        title: '',
        description: '',
        facilitator_id: userId,
        team_member_ids: [userId],
        max_participants: 15,
        duration_days: 30,
        status: 'active',
      });
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to create journey config. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      description: '',
      facilitator_id: userId,
      team_member_ids: [userId],
      max_participants: 15,
      duration_days: 30,
      status: 'active',
    });
    setError('');
    setSuccess('');
  };

  const isFormValid =
    formData.title &&
    formData.title.length >= 5 &&
    formData.facilitator_id &&
    formData.team_member_ids.length >= 1;

  const handleStartJourney = async () => {
    if (!createdConfig) return;

    try {
      setActivatingJourney(true);
      // Activate the config to create a journey instance
      const activateRes = await axios.post(
        `${API_URL}/api/journeys/${createdConfig.id}/activate`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log('Journey activated successfully:', activateRes.data);

      // Call the callback to close form and refresh dashboard
      if (onStartJourney) {
        console.log('Calling onStartJourney callback');
        onStartJourney(createdConfig.id);
      }
    } catch (err) {
      console.error('Journey activation error:', err);
      setError(
        err.response?.data?.message || 'Failed to activate journey'
      );
    } finally {
      setActivatingJourney(false);
    }
  };

  // If config created successfully, show success state only
  if (createdConfig) {
    return (
      <div className="form-card">
        <h2 className="form-title">Create New Innovation Journey</h2>
        {success && <div className="form-success">{success}</div>}

        <div className="form-actions">
          {onStartJourney && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleStartJourney}
              disabled={activatingJourney}
            >
              {activatingJourney ? 'Starting...' : 'Start Journey'}
            </button>
          )}

          {onViewAllJourneys && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={onViewAllJourneys}
            >
              View All Journeys
            </button>
          )}
        </div>

        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            setCreatedConfig(null);
            setSuccess('');
            handleReset();
          }}
          style={{ width: '100%', marginTop: '12px' }}
        >
          Create Another Journey
        </button>
      </div>
    );
  }

  return (
    <div className="form-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 className="form-title" style={{ margin: 0 }}>Create New Innovation Journey</h2>
        {onViewAllJourneys && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onViewAllJourneys}
            style={{ padding: '10px 20px', marginTop: 0 }}
          >
            View All Journeys
          </button>
        )}
      </div>

      {error && <div className="form-error">{error}</div>}
      {success && <div className="form-success">{success}</div>}

      <form onSubmit={handleSubmit}>
        {/* Journey Title */}
        <div className="form-group">
          <label>
            Journey Title <span className="required">*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="e.g., Mobile App Redesign"
            maxLength="100"
          />
          <div className="char-count">
            {formData.title.length}/100 characters
          </div>
        </div>

        {/* Description */}
        <div className="form-group">
          <label>Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="e.g., Redesign our mobile experience using design thinking"
            maxLength="500"
          />
          <div className="char-count">
            {formData.description.length}/500 characters
          </div>
        </div>

        {/* Facilitator */}
        <div className="form-group">
          <label>
            Facilitator <span className="required">*</span>
          </label>
          <select
            name="facilitator_id"
            value={formData.facilitator_id}
            onChange={handleInputChange}
          >
            <option value="">Select a facilitator...</option>
            {facilitators.map(f => (
              <option key={f.id} value={f.id}>{f.name} ({f.email})</option>
            ))}
          </select>
          {facilitators.length === 0 && (
            <small style={{ color: '#999', display: 'block', marginTop: '4px' }}>
              No facilitators available
            </small>
          )}
        </div>

        {/* Team Members */}
        <div className="form-group">
          <label>
            Team Members <span className="required">*</span> (min 1, max 50)
          </label>
          <div className="multi-select">
            {contributors.map(c => (
              <label key={c.id} className="multi-select-item">
                <input
                  type="checkbox"
                  checked={formData.team_member_ids.includes(c.id)}
                  onChange={() => handleTeamMemberToggle(c.id)}
                />
                {c.name} ({c.email})
              </label>
            ))}
          </div>
          <div className="selection-count">
            {formData.team_member_ids.length} member{formData.team_member_ids.length !== 1 ? 's' : ''} selected
          </div>
          {contributors.length === 0 && (
            <small style={{ color: '#999', display: 'block', marginTop: '4px' }}>
              No contributors available
            </small>
          )}
        </div>

        {/* Max Participants */}
        <div className="form-group">
          <label>
            Max Participants <span className="required">*</span> (3-100)
          </label>
          <input
            type="number"
            name="max_participants"
            value={formData.max_participants}
            onChange={handleInputChange}
            min="3"
            max="100"
          />
        </div>

        {/* Duration */}
        <div className="form-group">
          <label>
            Duration (Days) <span className="required">*</span> (7-365)
          </label>
          <input
            type="number"
            name="duration_days"
            value={formData.duration_days}
            onChange={handleInputChange}
            min="7"
            max="365"
          />
        </div>

        {/* Status */}
        <div className="form-group">
          <label>Status</label>
          <div className="radio-group">
            <div className="radio-option">
              <input
                type="radio"
                name="status"
                value="active"
                checked={formData.status === 'active'}
                onChange={handleInputChange}
              />
              <label>Active</label>
            </div>
            <div className="radio-option">
              <input
                type="radio"
                name="status"
                value="paused"
                checked={formData.status === 'paused'}
                onChange={handleInputChange}
              />
              <label>Paused</label>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleReset}
            disabled={loading}
          >
            Clear
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || !isFormValid}
          >
            {loading ? <span className="loading">⏳</span> : 'Create Journey'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default JourneyConfigForm;
